import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const ServiceContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const PageTitle = styled.h1`
  margin-bottom: var(--spacing-md);
  
  span {
    color: var(--accent-turquoise);
  }
`;

const IntroText = styled.p`
  max-width: 800px;
  margin-bottom: var(--spacing-xl);
  font-size: 1.2rem;
  color: var(--text-light);
`;

const ServiceSection = styled.section`
  margin-bottom: var(--spacing-xxl);
`;

const PackagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-xl);
  margin: var(--spacing-xl) 0;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
  }
`;

const Package = styled.div`
  background: white;
  border-radius: 8px;
  padding: var(--spacing-lg);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  border-top: 5px solid ${props => props.accentColor || 'var(--accent-turquoise)'};
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const PackageTitle = styled.h3`
  margin-bottom: var(--spacing-sm);
  font-size: 1.5rem;
  color: var(--text);
`;

const PackagePrice = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin: var(--spacing-md) 0;
  color: ${props => props.accentColor || 'var(--accent-turquoise)'};
`;

const PackageDescription = styled.p`
  margin-bottom: var(--spacing-md);
  color: var(--text-light);
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 var(--spacing-lg);
  flex-grow: 1;
`;

const Feature = styled.li`
  padding: var(--spacing-xs) 0;
  margin-bottom: var(--spacing-xs);
  display: flex;
  align-items: flex-start;
  
  &:before {
    content: "✓";
    color: ${props => props.accentColor || 'var(--accent-turquoise)'};
    margin-right: var(--spacing-xs);
    font-weight: bold;
  }
`;

const PackageButton = styled(Link)`
  display: inline-block;
  background-color: ${props => props.accentColor || 'var(--accent-turquoise)'};
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  text-align: center;
  border: 2px solid ${props => props.accentColor || 'var(--accent-turquoise)'};
  transition: all 0.3s ease;
  margin-top: auto;
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const FAQSection = styled.section`
  margin: var(--spacing-xxl) 0;
`;

const FAQItem = styled.div`
  margin-bottom: var(--spacing-md);
`;

const FAQQuestion = styled.h3`
  margin-bottom: var(--spacing-xs);
  color: var(--text);
`;

const FAQAnswer = styled.p`
  color: var(--text-light);
`;

const CTASection = styled.div`
  background-color: var(--secondary);
  padding: var(--spacing-xl);
  border-radius: 8px;
  text-align: center;
  margin-top: var(--spacing-xxl);
`;

const CTATitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: var(--spacing-md);
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const MonthlyMarketingSupport = () => {
  return (
    <ServiceContainer>
      <Container>
        <PageTitle>Monthly Marketing <span>Support</span></PageTitle>
        <IntroText>
          Consistent, strategic marketing support to help travel businesses grow steadily without the overhead 
          of a full-time marketing team. Our monthly packages are designed for both B2C and B2B travel businesses 
          looking to enhance their market presence and drive bookings.
        </IntroText>
        
        <ServiceSection>
          <h2>Choose Your Support Level</h2>
          <p>
            Each package is payable upfront on a monthly basis with no long-term contracts required.
            Select the level that matches your business needs and growth goals.
          </p>
          
          <PackagesGrid>
            <Package accentColor="var(--accent-lavender)">
              <PackageTitle>Essential</PackageTitle>
              <PackagePrice accentColor="var(--accent-lavender)">$495<span style={{ fontSize: "1rem" }}>/month</span></PackagePrice>
              <PackageDescription>
                Perfect for small travel operators looking to establish a consistent marketing presence.
              </PackageDescription>
              <FeatureList>
                <Feature accentColor="var(--accent-lavender)">Monthly strategy call (1 hour)</Feature>
                <Feature accentColor="var(--accent-lavender)">Performance dashboard setup</Feature>
                <Feature accentColor="var(--accent-lavender)">Social media content calendar</Feature>
                <Feature accentColor="var(--accent-lavender)">8 social posts/month for 2 platforms</Feature>
                <Feature accentColor="var(--accent-lavender)">1 monthly newsletter</Feature>
                <Feature accentColor="var(--accent-lavender)">Basic SEO keyword optimization</Feature>
                <Feature accentColor="var(--accent-lavender)">B2B outreach templates (travel agents, tour operators)</Feature>
                <Feature accentColor="var(--accent-lavender)">Monthly analytics report</Feature>
              </FeatureList>
              <PackageButton accentColor="var(--accent-lavender)" to="/contact?plan=essential">
                Get Started
              </PackageButton>
            </Package>
            
            <Package accentColor="var(--accent-turquoise)">
              <PackageTitle>Growth</PackageTitle>
              <PackagePrice accentColor="var(--accent-turquoise)">$995<span style={{ fontSize: "1rem" }}>/month</span></PackagePrice>
              <PackageDescription>
                Ideal for established businesses ready to accelerate their marketing efforts.
              </PackageDescription>
              <FeatureList>
                <Feature accentColor="var(--accent-turquoise)">Bi-weekly strategy calls (1 hour each)</Feature>
                <Feature accentColor="var(--accent-turquoise)">Performance dashboard with KPIs</Feature>
                <Feature accentColor="var(--accent-turquoise)">Social media content calendar</Feature>
                <Feature accentColor="var(--accent-turquoise)">16 social posts/month for 3 platforms</Feature>
                <Feature accentColor="var(--accent-turquoise)">2 email campaigns/month with segmentation</Feature>
                <Feature accentColor="var(--accent-turquoise)">Ongoing SEO optimization</Feature>
                <Feature accentColor="var(--accent-turquoise)">Quarterly content creation (blog/resources)</Feature>
                <Feature accentColor="var(--accent-turquoise)">B2B partnership development strategy</Feature>
                <Feature accentColor="var(--accent-turquoise)">Lead magnet creation (quarterly)</Feature>
                <Feature accentColor="var(--accent-turquoise)">Bi-weekly metrics analysis</Feature>
                <Feature accentColor="var(--accent-turquoise)">Quarterly market trend report</Feature>
              </FeatureList>
              <PackageButton accentColor="var(--accent-turquoise)" to="/contact?plan=growth">
                Get Started
              </PackageButton>
            </Package>
            
            <Package accentColor="var(--accent-blue)">
              <PackageTitle>Premium</PackageTitle>
              <PackagePrice accentColor="var(--accent-blue)">$1,995<span style={{ fontSize: "1rem" }}>/month</span></PackagePrice>
              <PackageDescription>
                Comprehensive marketing support for businesses seeking maximum growth.
              </PackageDescription>
              <FeatureList>
                <Feature accentColor="var(--accent-blue)">Weekly strategy calls</Feature>
                <Feature accentColor="var(--accent-blue)">Custom performance dashboard</Feature>
                <Feature accentColor="var(--accent-blue)">Full social media management (4 platforms)</Feature>
                <Feature accentColor="var(--accent-blue)">Daily social media content</Feature>
                <Feature accentColor="var(--accent-blue)">Complete email marketing with automation</Feature>
                <Feature accentColor="var(--accent-blue)">Comprehensive SEO strategy</Feature>
                <Feature accentColor="var(--accent-blue)">Monthly content creation</Feature>
                <Feature accentColor="var(--accent-blue)">Strategic B2B outreach campaign management</Feature>
                <Feature accentColor="var(--accent-blue)">Trade show support materials</Feature>
                <Feature accentColor="var(--accent-blue)">Lead generation system development</Feature>
                <Feature accentColor="var(--accent-blue)">Competitor analysis (monthly updates)</Feature>
                <Feature accentColor="var(--accent-blue)">Weekly analytics with actionable insights</Feature>
                <Feature accentColor="var(--accent-blue)">USP development and refinement</Feature>
                <Feature accentColor="var(--accent-blue)">Quarterly planning sessions</Feature>
              </FeatureList>
              <PackageButton accentColor="var(--accent-blue)" to="/contact?plan=premium">
                Get Started
              </PackageButton>
            </Package>
          </PackagesGrid>
        </ServiceSection>
        
        <ServiceSection>
          <h2>B2B Marketing Elements</h2>
          <p>
            Our monthly support packages include specialized B2B components to help travel operators connect 
            with key industry partners like travel agents, tour operators, and OTAs:
          </p>
          
          <FeatureList>
            <Feature>Partner outreach templates and scripts</Feature>
            <Feature>Trade network relationship development</Feature>
            <Feature>Travel agent FAM trip planning support</Feature>
            <Feature>Industry directory listing optimization</Feature>
            <Feature>B2B-focused content creation</Feature>
            <Feature>DMO and tourism board relationship strategy</Feature>
          </FeatureList>
        </ServiceSection>
        
        <FAQSection>
          <h2>Frequently Asked Questions</h2>
          
          <FAQItem>
            <FAQQuestion>Is there a minimum commitment period?</FAQQuestion>
            <FAQAnswer>
              No, all packages are month-to-month with upfront payment. You can adjust or cancel your plan at any time before your next billing cycle.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>Can I customize a package to fit my specific needs?</FAQQuestion>
            <FAQAnswer>
              Absolutely! While our packages provide excellent starting points, we're happy to discuss custom solutions that precisely match your business requirements.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>How do you track and report results?</FAQQuestion>
            <FAQAnswer>
              We set up custom dashboards that track your key performance indicators, including website traffic, social engagement, email metrics, and conversion rates. You'll receive regular reports based on your package level, and we'll review these together during our strategy calls.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>Who will be working on my account?</FAQQuestion>
            <FAQAnswer>
              You'll work with a dedicated marketing strategist who specializes in the travel industry, plus our team of specialists in content, social media, email, and SEO as needed for your package level.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>How does the B2B support work?</FAQQuestion>
            <FAQAnswer>
              We'll help you develop strategies for connecting with and nurturing relationships with travel agents, tour operators, and other industry partners. This includes developing professional materials, outreach campaigns, and follow-up systems specifically designed for B2B relationships in the travel industry.
            </FAQAnswer>
          </FAQItem>
        </FAQSection>
        
        <CTASection>
          <CTATitle>Ready to Elevate Your Travel Marketing?</CTATitle>
          <p>Let's discuss how our monthly marketing support can help your business achieve consistent growth.</p>
          <CTAButton to="/contact">Schedule Your Consultation</CTAButton>
        </CTASection>
      </Container>
    </ServiceContainer>
  );
};

export default MonthlyMarketingSupport; 